// Callout component — a transient label that floats up from a token on the
// map (hit/miss/damage, death-save success, etc.). Server-driven: the backend
// decides the text, color, and timing; the client owns the rise distance and
// easing curve.
//
// Animation runs as a single requestAnimationFrame loop driven off
// performance.now() with a fixed start reference. Phase boundaries are
// computed from elapsed time rather than chained setTimeouts so the timing
// stays stable under event-loop pressure and so multiple callouts in the
// same volley share a single timeline (no per-callout timer drift).

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';

class Callout extends BaseComponent {
  static properties = {
    text: { type: String },
    x: { type: Number },
    y: { type: Number },
    color: { type: String },
    delayMs: { type: Number, attribute: 'delay-ms' },
    holdMs: { type: Number, attribute: 'hold-ms' },
    fadeMs: { type: Number, attribute: 'fade-ms' },
    _offsetY: { type: Number, state: true },
    _opacity: { type: Number, state: true },
  };

  static styles = css`
    :host {
      display: block;
    }

    .callout {
      position: absolute;
      pointer-events: none;
      font-size: 16px;
      font-weight: normal;
      padding: 4px 8px;
      border-radius: 4px;
      white-space: nowrap;
      transform: translateX(-50%);
      z-index: 1000;
      text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
      background: rgba(0, 0, 0, 0.7);
      color: white;
    }
  `;

  constructor() {
    super();
    this.text = '';
    this.x = 0;
    this.y = 0;
    this.color = '';
    this.delayMs = 0;
    this.holdMs = 1500;
    this.fadeMs = 1500;
    // Hidden until the delay phase ends; without this a callout with a
    // non-zero delayMs would flash at full opacity for one frame before
    // the rAF loop runs and zeros it.
    this._offsetY = 0;
    this._opacity = 0;
    /** @type {number|null} */
    this._animationFrameId = null;
    this._fadeStartFired = false;
  }

  async connectedCallback() {
    await super.connectedCallback();

    const startTime = performance.now();
    const riseDistance = 50;

    const animate = (/** @type {number} */ currentTime) => {
      const elapsed = currentTime - startTime;
      const holdEnd = this.delayMs + this.holdMs;
      const fadeEnd = holdEnd + this.fadeMs;

      if (elapsed < this.delayMs) {
        // Pre-delay: hidden, no rise.
        this._opacity = 0;
        this._offsetY = 0;
      } else if (elapsed < holdEnd) {
        // Hold phase: fully visible, no rise.
        this._opacity = 1;
        this._offsetY = 0;
      } else if (elapsed < fadeEnd) {
        // Fade phase: opacity drops, label rises.
        if (!this._fadeStartFired) {
          this._fadeStartFired = true;
          this.dispatchEvent(new CustomEvent('fade-start', { bubbles: true }));
        }
        const fadeProgress = this.fadeMs > 0 ? (elapsed - holdEnd) / this.fadeMs : 1;
        const easeOut = 1 - (1 - fadeProgress) ** 3;
        this._offsetY = easeOut * riseDistance;
        this._opacity = 1 - easeOut;
      } else {
        // Animation complete.
        this._opacity = 0;
        this._offsetY = riseDistance;
        this.dispatchEvent(new CustomEvent('complete', { bubbles: true }));
        return;
      }

      this._animationFrameId = requestAnimationFrame(animate);
    };

    this._animationFrameId = requestAnimationFrame(animate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this._animationFrameId) {
      cancelAnimationFrame(this._animationFrameId);
    }
  }

  render() {
    const style = [
      `left: ${this.x}px`,
      `top: ${this.y - this._offsetY}px`,
      `opacity: ${this._opacity}`,
      this.color ? `color: ${this.color}` : '',
    ]
      .filter(Boolean)
      .join('; ');

    return html`<div class="callout" style="${style}">${this.text}</div>`;
  }
}

customElements.define('callout-label', Callout);
